import type { FunctionComponent, ReactNode, Ref } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { scmpPlus } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { section as sectionData } from "shared/data/section";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import type { aiArticleListHomeV2ArticleQuery } from "scmp-app/queries/__generated__/aiArticleListHomeV2ArticleQuery.graphql";

import { type AiArticleListVariant, type AIEngineResponse, useTracking } from "./hooks";
import {
  BottomItem1,
  BottomItem2,
  Container,
  Divider1,
  Divider2,
  Divider3,
  Divider4,
  Divider5,
  ItemsContainer,
  LeftItem1,
  LeftItem2,
  LeftItem3,
  MainItem,
  RightItem1,
  RightItem2,
  RightItem3,
  SectionTitle,
  SponsorItem,
} from "./styles";

export type Props = {
  aiEngineResponse: AIEngineResponse;
  className?: string;
  slots?: {
    nativeAds?: ReactNode;
    sponsorArticle?: (
      tracking: (entityId?: string) => void,
      ref?: Ref<HTMLDivElement>,
    ) => ReactNode;
  };
  // Recommended For You only: morning-studio queue edge for the sponsored card.
  // sponsoredReference?: aiArticleListHomeV2NativeAdQueueItemsEdge$key | null;
  variant: AiArticleListVariant;
};

const title: Record<Props["variant"], string> = {
  [AiEngineDataType.HomeRecommendedForYou]: "Recommended for you",
  [AiEngineDataType.HomeYouMayHaveMissed]: "You may be interested",
};

const customQueryParameterModule: Record<Props["variant"], string> = {
  [AiEngineDataType.HomeRecommendedForYou]: "AI_Recommended_for_you",
  [AiEngineDataType.HomeYouMayHaveMissed]: "AI_You_may_have_missed",
};

const displayArticleCount = 9;

export const HomeV2AiArticleList: FunctionComponent<Props> = ({
  aiEngineResponse: { aiEngineRecommendId, entityIds },
  className,
  slots,
  variant,
}) => {
  const queryReference = useLazyLoadQuery<aiArticleListHomeV2ArticleQuery>(
    graphql`
      query aiArticleListHomeV2ArticleQuery(
        $entityIdCount: Int!
        $entityIds: [String]
        $excludeSectionIds: [String]
        $scmpPlusPaywallTypeIds: [String]
      ) {
        contents(
          articleExcludeInput: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          exclude: { sectionIds: $excludeSectionIds }
          filter: { entityIds: $entityIds }
          first: $entityIdCount
          contentTypes: ARTICLE
        ) {
          edges {
            node {
              ... on Content {
                entityId
                ...homeSecondaryV2ContentItemContent
              }
            }
          }
        }
      }
    `,
    {
      entityIdCount: entityIds.length,
      entityIds,
      excludeSectionIds: [
        sectionData.posties.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.yp.entityUuid,
      ],
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
    },
    {
      fetchPolicy: "store-or-network",
    },
  );

  const articleMap = useMemo(
    () =>
      Object.fromEntries(
        (queryReference?.contents?.edges ?? []).map(({ node }) => [node.entityId, node]),
      ),
    [queryReference?.contents],
  );

  const displayAIArticles = entityIds
    .map(entityId => articleMap[entityId])
    .filter(node => notEmpty(node))
    .slice(0, displayArticleCount);

  const { captureTrackImpressionEventTargetElement, trackClick } = useTracking({
    aiEngineRecommendId,
    displayEntityIds: displayAIArticles.map(({ entityId }) => entityId),
    variant,
  });

  const handleClick = useCallback(
    (entityId?: string, urlAlias?: string) => {
      if (!notEmpty(entityId) || !notEmpty(urlAlias)) return;
      trackClick(entityId, getAbsoluteUrl(urlAlias));
    },
    [trackClick],
  );

  const sponsorArticleImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement: captureSponsorArticleImpressionEvent } =
    useTrackImpressionEventByIntersection({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: sponsorArticleImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (displayAIArticles.length === 0) return null;

  const itemComponents = [
    MainItem,
    LeftItem1,
    LeftItem2,
    LeftItem3,
    BottomItem1,
    BottomItem2,
    RightItem1,
    RightItem2,
    RightItem3,
  ];

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: `${customQueryParameterModule[variant]}_In-house`,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <SectionTitle>{title[variant]}</SectionTitle>
        <ItemsContainer>
          {displayAIArticles.map((node, index) => {
            const Component = itemComponents[index];
            switch (Component.displayName) {
              case "BottomItem1":
              case "BottomItem2":
                return (
                  <Component
                    isReadingTimeEnabled
                    key={node.entityId}
                    onClick={handleClick}
                    reference={node}
                    withComment
                    withImage
                    withSection
                  />
                );
              case "LeftItem1":
                return (
                  <Component
                    isReadingTimeEnabled
                    key={node.entityId}
                    onClick={handleClick}
                    reference={node}
                    withComment
                    withSection
                    withSummary
                  />
                );
              case "LeftItem2":
              case "LeftItem3":
                return (
                  <Component
                    isReadingTimeEnabled
                    key={node.entityId}
                    onClick={handleClick}
                    reference={node}
                    withComment
                    withSection
                  />
                );
              case "MainItem":
                return (
                  <Component
                    isReadingTimeEnabled
                    key={node.entityId}
                    onClick={handleClick}
                    reference={node}
                    withComment
                    withImage
                    withSection
                    withSummary
                  />
                );
              case "RightItem1":
              case "RightItem2":
              case "RightItem3":
                return (
                  <Component
                    isReadingTimeEnabled
                    key={node.entityId}
                    onClick={handleClick}
                    reference={node}
                    withComment
                    withSection
                  />
                );
              default:
                return <></>;
            }
          })}

          <SponsorItem>
            {slots?.nativeAds}
            {slots?.sponsorArticle?.(entityId => {
              sendGA4Tracking({
                action: "click",
                category: "recirculation",
                customized_parameters: {
                  article_id: entityId,
                  page_type: "homepage",
                  widget_name: "sponsor_article",
                },
                subcategory: "widget",
              });
            }, captureSponsorArticleImpressionEvent)}
          </SponsorItem>

          <Divider1 />
          <Divider2 />
          <Divider3 />
          <Divider4 />
          <Divider5 />
        </ItemsContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2AiArticleList.displayName = "HomeV2AiArticleList";
